import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  computed,
  inject,
  input,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import {
  CalendarOptions,
  DateSelectArg,
  DateSpanApi,
  DatesSetArg,
  EventApi,
  EventChangeArg,
  EventContentArg,
  EventInput,
  EventSourceFuncArg,
  EventSourceInput,
  ViewApi,
  globalPlugins,
} from '@fullcalendar/core';
import skLocale from '@fullcalendar/core/locales/sk';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, {
  EventDragStopArg,
  EventReceiveArg,
} from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
  ApiQuery,
  apiQueryHash,
} from '@kinet-open-network/client/core/api-client';
import { loading } from '@kinet-open-network/client/shared/util';
import { isEmpty } from '@kinet-open-network/shared/util';
import { hoursToMilliseconds } from 'date-fns';
import { Accordion, AccordionModule } from 'primeng/accordion';
import { MenuItem, TreeNode } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { DragDropModule } from 'primeng/dragdrop';
import { DropdownModule } from 'primeng/dropdown';
import { MenuModule } from 'primeng/menu';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SplitButton, SplitButtonModule } from 'primeng/splitbutton';
import { SplitterModule } from 'primeng/splitter';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';
import {
  CategorizedActions,
  buildCategorizedActions,
  processContextMenuActions,
  processDefaultAction,
} from '../resource-table/resource-actions';
import {
  RESOURCE_TABLE_STATE_KEY_PREFIX,
  TableFullConfig,
} from '../resource-table/resource-table.component';
import { SearchComponent } from '../search/search.component';
import {
  ResouceCalendarEventDirective,
  ResourceCalendarTemplateType,
} from './resource-calendar-event.directive';
import { ResourceCalendarUnscheduledCardComponent } from './resource-calendar-unscheduled-card.component';

export interface ICalendarResource<T = any> {
  id: string;
  title?: string;
  filters?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  data?: T;
}

export interface ICalendarResourceGroup<T = any> {
  id: string;
  title: string;
  data?: T;
  filters?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  calendars: ICalendarResource<T>[];
}

@Component({
  standalone: true,
  selector: 'kon-resource-calendar',
  imports: [
    CommonModule,
    RouterModule,
    FullCalendarModule,
    ResouceCalendarEventDirective,
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    SplitButtonModule,
    MenuModule,
    SelectButtonModule,
    BadgeModule,
    TooltipModule,
    ContextMenuModule,
    TreeSelectModule,
    SplitterModule,
    DragDropModule,
    DropdownModule,
    SearchComponent,
    ProgressBarModule,
    AccordionModule,
    ResourceCalendarUnscheduledCardComponent,
    TabViewModule,
  ],
  templateUrl: './resource-calendar.component.html',
  styleUrl: './resource-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceCalendarComponent<T> implements OnInit {
  /**
   * Controls calendar selection mode:
   * - 'single': show main calendar select and multi-select
   * - 'multi': hide main calendar select, show multi-select
   * - 'none': hide main calendar select, show only multi-select for groups
   */
  @Input() calendarSelectionMode: 'single' | 'multi' | 'none' = 'single';
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private stateKeyPrefix =
    inject(RESOURCE_TABLE_STATE_KEY_PREFIX, { optional: true }) ?? '';
  private injector = inject(Injector);

  @ContentChildren(ResouceCalendarEventDirective)
  templates!: QueryList<ResouceCalendarEventDirective>;
  @ViewChild('calendar') calendar?: FullCalendarComponent;
  @ViewChild('contextMenu') contextMenu?: ContextMenu;
  @ViewChild('unscheduled-accordion') unscheduledAccordion?: Accordion;
  @ViewChild('unscheduledEventsContainer')
  unscheduledEventsTemplate?: ElementRef<HTMLElement>;

  unscheduledCards = viewChildren<HTMLElement>('.unscheduled-event');

  @Input() loadEvents?: (query: ApiQuery) => Promise<T[] | undefined | null>;
  @Input() calendarDataToEntity?: (data: any | undefined) => Partial<T>;
  @Input() canCreate = false;
  @Output() eventChanged = new EventEmitter<T & { eventId?: string }>();

  disabledDates = input<Date[]>();

  actions: CategorizedActions<T> = {};
  isMobile = signal(false);

  constructor(breakpointObserver: BreakpointObserver) {
    breakpointObserver
      .observe(['(max-width: 639px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((state: any) => {
        this.isMobile.set(state.matches);
      });
  }

  startParam = input.required<keyof T>();
  endParam = input<keyof T>();
  unscheduledFilter = input<string>();
  idParam = input.required<keyof T>();
  parentIdParam = input<keyof T>();
  titleParam = input<keyof T>();
  unscheduledGroupBy = input<keyof T>();

  loading = loading();
  unscheduledEvents = signal<EventInput[]>([]);
  currentDate = signal<Date>(new Date());

  private dateToKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  private disabledDatesSet = computed(() => {
    const dates = this.disabledDates();
    if (!dates?.length) return new Set<string>();
    return new Set(dates.map((d) => this.dateToKey(d)));
  });

  private isSpanDisabled(start: Date, end: Date): boolean {
    const set = this.disabledDatesSet();
    if (!set.size) return false;
    const current = new Date(start);
    current.setHours(0, 0, 0, 0);
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);
    while (current <= endDay) {
      if (set.has(this.dateToKey(current))) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  }

  calendars = input.required<ICalendarResourceGroup[]>();
  calendarsFlat = computed(() =>
    this.calendars()?.flatMap((g) =>
      this.calendarSelectionMode === 'single' ? g.calendars : g,
    ),
  );
  calendarMap = computed(
    () =>
      new Map<string, ICalendarResource>(
        this.calendarsFlat().map((c) => [c.id, c]) ?? [],
      ),
  );

  unscheduledGrouped = computed(() => {
    const groupBy = this.unscheduledGroupBy();
    if (!groupBy) {
      return [];
    }
    const map = new Map<any, EventInput[]>();
    this.unscheduledEvents()?.forEach((e) => {
      const entity = e.extendedProps?.['entity'] as T;
      const key = entity[groupBy] as any;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)?.push(e);
    });
    return Array.from(map.entries())
      .map(([key, events]) => ({
        key,
        events,
      }))
      .sort((a, b) => a.key?.localeCompare(b.key) ?? -1);
  });

  global = [globalPlugins];

  private _config: TableFullConfig<T> = {
    sortable: true,
    paginator: false,
    perPage: 25,
    perPageOptions: [25, 50, 100],
    header: {
      newButton: {
        label: 'Pridať',
        routerLink: ['create'],
        icon: 'pi pi-plus',
      },
    },
  };

  @Input()
  set config(cfg: Partial<TableFullConfig<T>>) {
    this._config = {
      ...this._config,
      ...cfg,
      header: {
        ...this._config.header,
        ...cfg.header,
      },
    };
    this.actions = buildCategorizedActions(this._config.actions ?? []);
  }
  get config(): TableFullConfig<T> {
    return this._config;
  }

  get stateKey() {
    return this.config.stateKey
      ? `${this.stateKeyPrefix}calendar:${this.config.stateKey}`
      : undefined;
  }

  get splitterStateKey() {
    return this.config.stateKey
      ? `${this.stateKeyPrefix}splitter:${this.config.stateKey}`
      : undefined;
  }

  private _state?: CalendarState;
  get state(): CalendarState {
    if (!this._state) {
      let state: CalendarState = {};
      if (this.stateKey) {
        const stateString = localStorage.getItem(this.stateKey);
        if (stateString) {
          state = JSON.parse(stateString) as CalendarState;
        }
      }
      this._state = {
        view: state.view ?? 'timeGridWeek',
        date: state.date ? new Date(state.date) : new Date(),
        search: state.search,
        searchUnscheduled: state.searchUnscheduled,
        filterUnscheduled: state.filterUnscheduled ?? false,
        calendars: state.calendars ?? [],
        onlyWorkHours: state.onlyWorkHours ?? false,
        calendar: state.calendar,
      } as CalendarState;
      this.stateSignal.set(this._state);
    }
    return this._state ?? {};
  }

  stateSignal = signal<CalendarState | undefined>(undefined);

  patchState(state: Partial<CalendarState>) {
    this._state = {
      ...this.state,
      ...state,
    };
    if (!this.stateKey) {
      return;
    }
    localStorage.setItem(this.stateKey, JSON.stringify(this._state));
    this.stateSignal.set(this._state);
  }

  refetchEvents(force?: boolean) {
    this.calendar?.getApi()?.refetchEvents();
    this.fetchUnscheduledEvents(force);
  }

  @ViewChild('splitButton') splitButton?: SplitButton;

  handleHeaderButtonClick(event: MouseEvent) {
    const btnConfig = this.config.header?.newButton;

    if (!btnConfig) {
      return;
    }

    if (btnConfig.routerLink) {
      const routerLink = Array.isArray(btnConfig.routerLink)
        ? btnConfig.routerLink
        : [btnConfig.routerLink];

      this.router.navigate(routerLink, {
        relativeTo: this.route,
      });
    } else if (btnConfig.command) {
      btnConfig.command();
    } else if (this.splitButton) {
      setTimeout(() => this.splitButton?.onDropdownButtonClick(event));
    }
  }

  contextMenuItems: MenuItem[] | undefined = undefined;

  onContextMenuSelectionChange(event: any, entity: T) {
    this.contextMenuItems = processContextMenuActions(entity, this.actions);
    if (this.contextMenu) {
      this.contextMenu.target = event.target;
      this.contextMenu.show(event);
    }
  }

  getWorkHoursStart() {
    return this.state.onlyWorkHours ? '06:00:00' : '00:00:00';
  }

  getWorkHoursEnd() {
    return this.state.onlyWorkHours ? '18:00:00' : '24:00:00';
  }

  toggleWorkHours() {
    this.patchState({
      onlyWorkHours: !this.state.onlyWorkHours,
    });
    if (this.calendar?.options) {
      this.calendar.options.slotMinTime = this.getWorkHoursStart();
      this.calendar.options.slotMaxTime = this.getWorkHoursEnd();
    }
  }

  calendarOptions = computed(
    () =>
      ({
        initialView: this.state?.view ?? 'timeGridWeek',
        initialDate: this.state?.date ?? new Date(),
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        headerToolbar: false,
        selectable: true,
        editable: true,
        droppable: true,
        locale: skLocale,
        locales: [skLocale],
        scrollTime: '06:00:00',
        slotMinTime: this.getWorkHoursStart(),
        slotMaxTime: this.getWorkHoursEnd(),
        aspectRatio: 0.5,
        height: '100%',
        views: {
          timeGridDay: {
            type: 'timeGrid',
            duration: { days: 1 },
            buttonText: 'Deň',
          },
          timeGridWorkWeek: {
            type: 'timeGrid',
            duration: { week: 1 },
            weekends: false,
            buttonText: 'Pracovný týždeň',
          },
          timeGridWeek: {
            type: 'timeGrid',
            duration: { week: 1 },
            weekends: true,
            buttonText: 'Týždeň',
          },
          dayGridMonth: {
            type: 'dayGrid',
            buttonText: 'Mesiac',
            duration: { months: 1 },
          },
        },
        eventSources: this.calendarsFlat()?.map((c) => {
          return {
            events: async (arg: EventSourceFuncArg) => {
              if (!this.isCalendarEnabled(c.id)) {
                return [];
              }
              this.loading.loading(c.id);
              const query = {
                perPage: -1,
                filters: this.buildEventSourceFilters(arg, c.filters),
                search: isEmpty(this.state.search)
                  ? undefined
                  : this.state.search,
              } as ApiQuery;
              const events = await this.loadEvents?.(query)
                ?.then((events) => this.mapEntityToEvent(events))
                .finally(() => this.loading.finished(c.id));
              this.patchState({
                date: this.calendar?.getApi()?.getDate(),
              });
              const disabled = this.disabledDates() ?? [];
              const disabledEvents = disabled
                .filter((d) => {
                  const dayStart = new Date(
                    d.getFullYear(),
                    d.getMonth(),
                    d.getDate(),
                  );
                  return dayStart >= arg.start && dayStart < arg.end;
                })
                .map(
                  (date, idx) =>
                    ({
                      backgroundColor: 'var(--color-danger-100)',
                      className: 'fc-disabled-date',
                      start: new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                      ),
                      allDay: true,
                      display: 'background',
                      id: `disabled-${idx}`,
                      title: 'Deň je zablokovaný',
                      interactive: false,
                      extendedProps: {
                        entity: {},
                        disabled: true,
                      },
                    } as EventInput),
                );
              return [...(events || []), ...disabledEvents];
            },
            id: c.id,
          } as EventSourceInput;
        }),
        selectAllow: (span: DateSpanApi) => {
          const end = new Date(span.end.getTime() - 1);
          return !this.isSpanDisabled(span.start, end);
        },
        eventAllow: (span: DateSpanApi) => {
          const end = new Date(span.end.getTime() - 1);
          return !this.isSpanDisabled(span.start, end);
        },
        datesSet: (arg: DatesSetArg) => {
          this.currentDate.set(arg.view.currentStart);
        },
        select: (arg: DateSelectArg) => {
          if (!this.canCreate) {
            return;
          }
          const queryParams: any = {
            [this.startParam()]: arg.start.toISOString(),
            ...this.calendarDataToEntity?.(this.selectedCalendar?.data),
          };
          if (this.endParam()) {
            queryParams[this.endParam() as string] = arg.end.toISOString();
          }
          this.router.navigate(['create'], {
            relativeTo: this.route,
            queryParams,
          });
        },
        eventChange: (arg: EventChangeArg) => {
          const entity = arg.event.extendedProps['entity'] as T;
          const disabled = arg.event.extendedProps['disabled'] as boolean;
          if (entity && !disabled) {
            (entity as any)[this.startParam()] = arg.event.start;
            if (this.endParam()) {
              (entity as any)[this.endParam() as string] = arg.event.end;
            }
            this.eventChanged.emit(entity);
          }
        },
        eventReceive: (arg: EventReceiveArg) => {
          if (arg.event.allDay && arg.event.start) {
            arg.event.setEnd(
              new Date(arg.event.start.getTime() + hoursToMilliseconds(24)),
            );
            arg.event.setAllDay(true);
          }

          const entity = arg.event.extendedProps?.['entity'] as T;
          if (entity) {
            (entity as any)[this.startParam()] = arg.event.start;
            if (this.endParam()) {
              (entity as any)[this.endParam() as string] = arg.event.end;
            }
            this.eventChanged.emit({
              ...entity,
              ...this.calendarDataToEntity?.(this.selectedCalendar?.data),
              eventId: arg.event.id,
            });

            const remove = this.unscheduledEvents().filter(
              (e) =>
                e.extendedProps?.['entity'].id !==
                arg.event.extendedProps?.['entity'].id,
            );
            this.unscheduledEvents.set(remove);
          }
        },
        eventDragStop: (arg: EventDragStopArg) => {
          if (!this.unscheduledEventsTemplate?.nativeElement) {
            return;
          }
          const unscheduledRect =
            this.unscheduledEventsTemplate.nativeElement.getBoundingClientRect();
          let mouseX: number | undefined;
          let mouseY: number | undefined;

          if (arg.jsEvent instanceof MouseEvent) {
            mouseX = arg.jsEvent.clientX;
            mouseY = arg.jsEvent.clientY;
          } else if ((arg as any).jsEvent instanceof TouchEvent) {
            if ((arg as any).jsEvent.changedTouches.length > 0) {
              mouseX = (arg as any).jsEvent.changedTouches?.[0]?.clientX;
              mouseY = (arg as any).jsEvent.changedTouches?.[0]?.clientY;
            }
          }

          if (mouseX === undefined || mouseY === undefined) {
            console.warn(
              'Could not determine mouse/touch coordinates during eventDragStop.',
            );
            return;
          }

          const isInside =
            mouseX >= unscheduledRect.left &&
            mouseX <= unscheduledRect.right &&
            mouseY >= unscheduledRect.top &&
            mouseY <= unscheduledRect.bottom;

          if (isInside) {
            arg.event.remove();
            const entity = arg.event.extendedProps?.['entity'];
            if (entity) {
              (entity as any)[this.startParam()] = null;
              (entity as any)[this.endParam()] = null;

              const newEvent: EventInput = {
                ...arg.event.toPlainObject(),
                start: undefined,
                end: undefined,
                allDay: false,
                extendedProps: { entity },
              };

              this.unscheduledEvents.set([
                ...this.unscheduledEvents().filter(
                  (e) => e.extendedProps?.['entity']?.id !== entity.id,
                ),
                newEvent,
              ]);

              this.eventChanged.emit({
                ...entity,
                eventId: arg.event.id,
              });
            }
          }
        },
      } as CalendarOptions),
  );

  refetchEventBySourceId(id: string) {
    const source = this.calendar?.getApi()?.getEventSourceById(id);
    return source?.refetch();
  }

  deleteEventById(id: string) {
    const event = this.calendar?.getApi()?.getEventById(id);
    if (event) {
      event.remove();
    }
  }

  private lastUnsheduledLoadHash: string | undefined = undefined;

  async fetchUnscheduledEvents(force = false) {
    let filters: string | undefined = undefined;
    if (
      this.filterUnscheduled() &&
      (this.selectedCalendar || this.selectedCalendars.length > 0)
    ) {
      filters = this.calendarsFlat()
        .filter((c) => this.isCalendarEnabled(c.id) && c.filters)
        .map((c) => `(${c.filters})`)
        .join(' or ');
    }

    if (this.loadEvents) {
      const loadingKey = `unscheduled-${Math.round(Math.random() * 1000)}`;
      this.loading.loading(loadingKey);
      const query = {
        perPage: -1,
        filters: this.buildEventSourceFilters('not-scheduled', filters),
        search: isEmpty(this.state.searchUnscheduled)
          ? undefined
          : this.state.searchUnscheduled,
      } as ApiQuery;

      const queryHash = apiQueryHash(query);

      // If the query didn't change, don't reload
      if (!force && this.lastUnsheduledLoadHash === queryHash) {
        this.loading.finished(loadingKey);
        return;
      }

      this.lastUnsheduledLoadHash = queryHash;

      this.loadEvents(query)
        .then((events) => this.mapEntityToEvent(events))
        .then((events) => {
          this.unscheduledEvents.set(events ?? []);
        })
        .finally(() => this.loading.finished(loadingKey));
    }
  }

  mapEntityToEvent(entities: T[] | null | undefined): EventInput[] | undefined {
    const calendarMap = this.calendarMap();
    return entities?.map((entity) => {
      const titleParam = this.titleParam();
      const id = entity[this.idParam()]?.toString();
      const start = entity[this.startParam()] as Date;
      let end: Date | undefined = undefined;
      if (this.endParam()) {
        end = entity[this.endParam() as keyof T] as Date;
      }
      if (start && !end) {
        end = new Date(start.getTime() + hoursToMilliseconds(2));
      }
      const allDay =
        start &&
        end &&
        end.getTime() - start.getTime() >= hoursToMilliseconds(24);
      const title = titleParam ? entity[titleParam] : '';

      const parentIdParam = this.parentIdParam();
      const parentId = parentIdParam ? entity[parentIdParam]?.toString() : null;

      const calendar = id ? calendarMap.get(id) : null;
      const parentCalendar = parentId ? calendarMap.get(parentId) : null;

      const def = {
        backgroundColor:
          calendar?.backgroundColor ??
          parentCalendar?.backgroundColor ??
          'var(--color-primary-100)',
        textColor:
          calendar?.textColor ??
          parentCalendar?.textColor ??
          'var(--color-primary-600)',
        borderColor:
          calendar?.borderColor ??
          parentCalendar?.borderColor ??
          'var(--color-primary-600)',
      };

      return {
        ...def,
        id: (entity as any)['id'],
        start,
        end,
        allDay,
        title,
        extendedProps: {
          entity,
        },
      } as EventInput;
    });
  }

  @Input() defaultSelectedCalendar?: Partial<TreeNode>;

  private _selectedCalendar?: Partial<TreeNode>;

  get selectedCalendar() {
    if (!this._selectedCalendar && this.calendarSelectionMode === 'single') {
      this._selectedCalendar =
        this.state?.calendar ?? this.defaultSelectedCalendar;
    }

    return this._selectedCalendar as Partial<TreeNode>;
  }

  set selectedCalendar(calendar: Partial<TreeNode>) {
    if (!calendar && this.calendarSelectionMode === 'single') {
      calendar = this.defaultSelectedCalendar as Partial<TreeNode>;
    }
    this._selectedCalendar = calendar;
    this.patchState({
      calendar: {
        key: calendar.key,
        label: calendar.label,
        data: calendar.data,
      },
    });
    this.refetchEvents();
  }

  private _selectedCalendars?: Partial<TreeNode>[];

  get selectedCalendars() {
    if (!this._selectedCalendars) {
      this._selectedCalendars = this.state?.calendars ?? [];
    }
    return this._selectedCalendars;
  }

  set selectedCalendars(calendars: any[]) {
    this._selectedCalendars = calendars;
    this.patchState({
      calendars: this._selectedCalendars.map((c) => {
        return {
          key: c.key,
          label: c.label,
          partialSelected: c.partialSelected,
        };
      }),
    });
    this.refetchEvents();
  }

  getCalendarDef(id: string) {
    return this.calendarsFlat()?.find((c) => c.id === id);
  }

  isCalendarEnabled(key: string) {
    if (this.calendarSelectionMode === 'none') {
      return true;
    }
    if (this.calendarSelectionMode === 'single') {
      return this.selectedCalendar?.key === key;
    }
    return (
      this.selectedCalendar?.key === key ||
      !!this.selectedCalendars?.find((c) => c.key === key)
    );
  }

  selectCalendarItems = computed(() =>
    this.calendars()
      ?.map((group) => ({
        ...this.mapToTree(group),
        selectable: false,
        children: group.calendars
          .filter((item) => item.id !== group.id)
          .map((cal) => this.mapToTree(cal))
          .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '')),
      }))
      ?.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '')),
  );

  selectMoreCalendarItems = computed<TreeNode[]>(() =>
    this.calendarSelectionMode === 'none'
      ? this.calendars()
          ?.map((group) => this.mapToTree(group))
          ?.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))
      : this.calendars()
          ?.map((group) => ({
            ...this.mapToTree(group),
            children: group.calendars
              ?.map((cal) => this.mapToTree(cal))
              ?.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '')),
          }))
          ?.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '')),
  );

  mapToTree = (item: ICalendarResourceGroup | ICalendarResource) =>
    ({
      key: item.id,
      label: item.title,
      styleClass: 'whitespace-nowrap',
      data: 'data' in item ? item.data : undefined,
    } as TreeNode);

  buildEventSourceFilters(
    arg: EventSourceFuncArg | 'not-scheduled',
    additionalFilters?: string,
  ) {
    let dateFilter = '';
    if (arg === 'not-scheduled') {
      const unscheduledFilter = this.unscheduledFilter();
      if (unscheduledFilter) {
        dateFilter = unscheduledFilter;
      } else {
        dateFilter = `not(${this.startParam().toString()} pr)`;
      }
    } else if (this.endParam()) {
      dateFilter = `(not (${this.endParam()?.toString()} pr) or ${this.endParam()?.toString()} gt "${arg.start.toISOString()}") and ${this.startParam().toString()} lt "${arg.end.toISOString()}"`;
    } else {
      dateFilter = `${this.startParam().toString()} lt "${arg.end.toISOString()}"`;
    }
    return (
      dateFilter + (additionalFilters ? ` and (${additionalFilters})` : '')
    );
  }

  getEventTemplate(type: ResourceCalendarTemplateType) {
    return this.templates.find((t) => t.type === type)?.template;
  }

  getTemplateContext(
    event: EventApi | EventInput,
    view: ViewApi | null,
    data: EventContentArg | null,
  ) {
    return {
      $implicit: event,
      event,
      view,
      data,
      entity: event?.extendedProps?.['entity'],
    };
  }

  getStartTime(event: EventApi | null) {
    return (
      event?.start?.toLocaleTimeString('sk-SK', {
        hour: '2-digit',
        minute: '2-digit',
      }) ?? ''
    );
  }

  views = computed(() => {
    return Object.entries(this.calendarOptions().views ?? {})
      .map(([key, view]) => ({
        key,
        view: view as any,
      }))
      .filter((view) => view.view.type);
  });

  viewActions = computed(() => {
    const currentView = this.stateSignal()?.view;

    return [
      {
        label: 'Zobrazenie',
        items: [
          ...(this.views()?.map((view) => ({
            label: view.view.buttonText,
            icon:
              currentView === view.key
                ? 'pi pi-check-square text-primary'
                : 'pi pi-calendar',
            styleClass: 'p-menuitem-link-active',
            command: () => {
              this.calendar?.getApi()?.changeView(view.key);
              this.patchState({
                view: view.key,
              });
            },
          })) ?? []),
          {
            separator: true,
          },
          {
            label: 'Len pracovné hodiny',
            icon: this.stateSignal()?.onlyWorkHours
              ? 'pi pi-check-square text-primary'
              : 'pi pi-clock',
            command: () => this.toggleWorkHours(),
          },
        ],
      },
    ] as MenuItem[];
  });

  getDefaultAction(entity: T) {
    return processDefaultAction(entity, this.actions);
  }

  searchText = signal<string | null>(null);
  filterUnscheduled = computed(
    () => this.stateSignal()?.filterUnscheduled ?? false,
  );

  onSearchChange(searchUnscheduled: string | null) {
    this.patchState({ searchUnscheduled });
    this.fetchUnscheduledEvents();
  }

  toggleFilterUnscheduled() {
    this.patchState({ filterUnscheduled: !this.filterUnscheduled() });
    this.refetchEvents();
  }

  splitterResizeEnd(event: any) {
    this.calendar?.getApi().updateSize();
  }

  ngOnInit(): void {
    this.searchText.set(this.state.searchUnscheduled ?? null);

    toObservable(this.calendars, { injector: this.injector }).subscribe(
      (calendars) => {
        if (calendars.length > 0) {
          this.fetchUnscheduledEvents();
        }
      },
    );

    if (
      !this.selectedCalendars?.length &&
      this.calendarSelectionMode !== 'single' &&
      this.defaultSelectedCalendar
    ) {
      this._selectedCalendars = [this.defaultSelectedCalendar];
    }
  }

  unscheduledVisible = signal(false);

  toggleUnscheduled() {
    this.unscheduledVisible.set(!this.unscheduledVisible());
    setTimeout(() => {
      this.calendar?.getApi().updateSize();
    });
  }
}

interface CalendarState {
  view?: string;
  date?: Date;
  search?: string | null;
  searchUnscheduled?: string | null;
  filterUnscheduled?: boolean;
  onlyWorkHours?: boolean;
  calendar?: Partial<TreeNode>;
  calendars?: Partial<TreeNode>[];
}
